import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { detectCountryDomain } from '../lib/country-domain';
import en from './locales/en.json';
import nl from './locales/nl.json';
import ptBR from './locales/pt-BR.json';
import fr from './locales/fr.json';
import es from './locales/es.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'nl', label: 'Nederlands', short: 'NL' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'pt-BR', label: 'Português', short: 'PT' },
  { code: 'es', label: 'Español', short: 'ES' },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

// Seed the UI language from the country domain (.pt→pt-BR, .be/.nl→nl, .fr→fr)
// ONLY on a first visit. An explicit stored choice always wins.
const languageDetector = new LanguageDetector();
languageDetector.addDetector({
  name: 'countryDomain',
  lookup() {
    // Respect an explicit prior choice; only seed the default on first visit.
    try {
      if (localStorage.getItem('cb_market_lng2')) return undefined;
    } catch {
      /* localStorage unavailable → fall through to domain default */
    }
    const { code, defaultLang } = detectCountryDomain();
    return code ? defaultLang : undefined;
  },
  cacheUserLanguage() {
    /* no-op: caching handled by the localStorage detector */
  },
});

i18n
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      nl: { translation: nl },
      fr: { translation: fr },
      pt: { translation: ptBR },
      'pt-BR': { translation: ptBR },
      'pt-PT': { translation: ptBR },
      es: { translation: es },
    },
    fallbackLng: 'en',
    supportedLngs: ['en', 'nl', 'fr', 'pt', 'pt-BR', 'pt-PT', 'es'],
    load: 'currentOnly',
    debug: false,
    interpolation: { escapeValue: false },
    detection: {
      // No 'htmlTag' detector: index.html hardcodes lang="en", and i18next
      // prefers any exact supported match ('en') over mapping regional
      // locales (nl-BE/fr-BE/…) to their base language — so htmlTag forced
      // English on every regional-locale visitor. Storage key is bumped to
      // _lng2 because the old key holds the 'en' that bug cached.
      order: ['localStorage', 'countryDomain', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'cb_market_lng2',
    },
  });

const applyHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng.split('-')[0];
  }
};
applyHtmlLang(i18n.resolvedLanguage || i18n.language || 'en');
i18n.on('languageChanged', applyHtmlLang);

export default i18n;
